import { useState, useMemo } from 'react';

import { i18n } from '../../../../../translate/i18n';

import { 
  StableSelect, 
  RetailSelect, 
  TableDownloadButton 
} from '../../../../../components/SharedComponents/sharedComponents.index';
import { CustomText } from '../../../../../components/StyledComponents/styleComponents.index';

import { SearchIcon } from '../../../../../modules/PurchaseManagementModule/PurchaseHistory/components/Icons/SearchIcon';
import { COMPANY_STORE_ID } from '../../../../../components/components.constants';

const { t } = i18n;

const ClearIcon = ({ color = "#9CA3AF", onClick }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={28}
    height={28}
    viewBox="0 0 24 24"
    onClick={onClick}
    style={{ cursor: "pointer" }}
  >
    <line x1="4" y1="4" x2="20" y2="20" stroke={color} strokeWidth="2" />
    <line x1="20" y1="4" x2="4" y2="20" stroke={color} strokeWidth="2" />
  </svg>
);

const storeName = 'caedu'

export const HeaderRetail = ({ actions, ctx }) => {

  const { 
    setCurrentStoreData, 
    setSelectedSecondaryFilterValues, 
    setSeason,
    setCompanyArea = () => {},
    setToggleBreakdownData = () => {}
  } = actions || {};
  
  const { 
    season,
    selectedSecondaryFilterValues,
    data,
    allAvailableSeasons,
    areas,
    currentStoreId,
    currentCompanyArea,
    toggleBreakdownData = false
  } = ctx || {};

  const [searchInput, setSearchInput] = useState("");

  // opções de lojas
  const hasActiveFilter = Boolean(selectedSecondaryFilterValues);

  const isCaedu = storeName === 'caedu';

  const storeOptions = useMemo(() => {
    const sorted = [...(data || [])].sort((a, b) => {
      if (a.storeId === COMPANY_STORE_ID.STORE_ID) return -1;
      if (b.storeId === COMPANY_STORE_ID.STORE_ID) return 1;
     
      return a.storeId - b.storeId;
    });

    return sorted.map(store => ({
      value: store?.storeId,
      label: store?.storeId === COMPANY_STORE_ID.STORE_ID
        ? t("company")
        : (isCaedu && store?.level3) || `${t("store")} ${store?.storeId}`
    }));
  }, [data, isCaedu]);

  const storeFieldWidth = useMemo(() => {
    if (!isCaedu) return "5rem";
    const selectedLabel = storeOptions.find(option => Number(option.value) === Number(currentStoreId))?.label ?? "";
    return `${Math.max(String(selectedLabel).length + 2, 6)}ch`;
  }, [storeOptions, currentStoreId, isCaedu]);

  const handleStoreChange = (selectedStoreId) => {
    const storeData = data.find(s => s.storeId === selectedStoreId);
    if (storeData) {
      setCurrentStoreData(storeData.storeId);
    }
  };

  // opções de coleções
  const seasonOptions = allAvailableSeasons?.map(season => ({
    value: season?.seasonId,
    label: season?.seasonName
  })) || [];

  const seasonNameToIdMap = useMemo(() => {
    if (!allAvailableSeasons?.length) return {};
    return allAvailableSeasons.reduce((acc, s) => {
      acc[s.seasonName] = s.seasonId;
      return acc;
    }, {});
  }, [allAvailableSeasons]);

  const seasonMap = useMemo(() => {
    if (!allAvailableSeasons?.length) return {};
    return allAvailableSeasons.reduce((acc, s) => {
      acc[s.seasonId] = s.seasonName;
      return acc;
    }, {});
  }, [allAvailableSeasons]);

  const handleSeasonChange = (selectedIds) => {
    const selectedId = selectedIds[0];
    if (!selectedId) {
      setSeason([]);
      return;
    }
    const seasonName = seasonMap[selectedId];
    setSeason(seasonName ? [seasonName] : []);
  };

  // opções de áreas de Atuação
  const companyAreasOptions = [
    { value: 0, label: "Todas as áreas" },
    ...(areas?.map(area => ({
      value: area?.areaId,
      label: area?.label
    })) || [])
  ];

  const handleAreaChange = (selectedIds) => {
    const zeroIsNew = selectedIds.includes(0) && currentCompanyArea.length > 0;
    if (zeroIsNew) {
      setCompanyArea([]);
      return;
    }
    setCompanyArea(selectedIds.filter(id => id !== 0));
  };

  return (
    <div style={{ 
      display: "flex", 
      flexWrap: "nowrap",
      alignItems: "center",
      gap: '1rem',
      backgroundColor: "#fff", 
      padding: "1rem", 
      borderRadius: "0.6rem", 
      borderColor: "rgba(17, 24, 39, 0.125)", 
      borderStyle: "solid", 
      borderWidth: "1px", 
      boxSizing: "border-box"}}>
        
        {/* Buscar por produto */}
        <section style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "start",
          gap: "0.4rem",
          flexDirection: "row"}}>
          <CustomText fontSize={['0.4rem', '0.6rem', '0.8rem']} weight="bold">
            {t("searchByProduct")}
          </CustomText>

          <div style={{
            backgroundColor: 'rgb(250,250,250)',
            borderRadius: '0.6rem',
            borderColor: 'rgba(17, 24, 39, 0.125)',
            borderStyle: 'solid',
            borderWidth: '1px',
            boxSizing: 'border-box',
            color: 'rgb(153, 153, 153)',
            fontSize: '0.75rem',
            fontWeight: 700,
            lineHeight: '16.1px',
            inlineSize: "14rem",
            paddingInline: "0.4rem",
            overflow: 'hidden' }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                inlineSize: "100%",
                maxInlineSize: "100%",
                height: "2.2rem",
                borderRadius: "0.4rem",}}>

                <input
                  type="text"
                  placeholder="Digite..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{
                    inlineSize: "100%",
                    blockSize: "100%",
                    background: "transparent",
                    border: "none",
                    outline: "none",
                    color: "rgb(55, 65, 81)",
                    caretColor: "rgb(55, 65, 81)",
                    fontSize: "14px",
                    fontWeight: 400,
                  }}/>

                {!hasActiveFilter ? (
                  <SearchIcon
                    color="#FF6600"
                    onClick={() => { setSelectedSecondaryFilterValues(searchInput); }}/>
                ) : (
                  <ClearIcon
                    onClick={() => {
                      setSelectedSecondaryFilterValues("");
                      setSearchInput("");
                    }}
                  />
                )}
              </div>
          </div>

        </section>

        {/* Lojas */}
        <section style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "row",
          flexShrink: 0,
          gap: "0.4rem",
          marginInlineStart: "0.5rem",
          marginInlineEnd: "0.8rem" }}>
          <CustomText fontSize={['0.4rem', '0.6rem', '0.8rem']} weight="bold">
            {t("channel")}
          </CustomText>
          
          <StableSelect
            label={""}
            options={storeOptions}
            value={currentStoreId}
            onChange={handleStoreChange}
            width=""
            fieldWidth={storeFieldWidth}/>

        </section>

        {/* Áreas de atuação */}
        <section style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "row",
          flexShrink: 0,
          gap: "0.5rem",
          marginInlineStart: "0.5rem",
          marginInlineEnd: "1rem" }}>
          <CustomText fontSize={['0.4rem', '0.6rem', '0.8rem']} weight="bold" whiteSpace="nowrap">
            {"Áreas de Atuação"}
          </CustomText>

          <RetailSelect
            options={companyAreasOptions}
            value={currentCompanyArea.length === 0 ? [0] : currentCompanyArea}
            onChange={handleAreaChange}
            width={195}/>
        </section>

        {/* Dados de quebra */}
        <section
          style={{
            display: "flex",
            flexShrink: 0,
            marginInlineStart: "0.75rem",
            justifyContent: "center",
            alignItems: "center" }}>
          <p style={{ display: "flex", flexDirection: "column", fontSize: "0.8rem", fontWeight: "bold",  }}>
            <span>Exibir <span style={{ color: "#e75d0d", paddingInlineEnd: "0.8rem" }}>Dados</span>{" "}</span>
            <span style={{ color: "#e75d0d" }}> de Quebra</span>{" "}
          </p>

          <label className="switch">
            <input
              type="checkbox"
              checked={toggleBreakdownData}
              onChange={(e) => setToggleBreakdownData(e.target.checked)}/>
            <span className="slider" />
          </label>
        </section>    

        {/* Coleções */}
        <section style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-around",
          flexDirection: "row",
          flexShrink: 0,
          gap: "1rem",
          marginInlineStart: "0.5rem"}}>
          <CustomText fontSize={['0.4rem', '0.6rem', '1rem']} weight="bold">
            {t("collection")}
          </CustomText>

          <RetailSelect
            options={seasonOptions}
            value={season.length === 0 ? [0] : season.map(name => seasonNameToIdMap[name]).filter(Boolean)}
            onChange={handleSeasonChange}
            multiple={false}
            width={195}
            disabled={false}/>

        </section>

        <div style={{ marginInlineStart: "0.5rem", flexShrink: 0 }}>
          <TableDownloadButton
            ctx={{
              tableId: "adjustments-supply-table",
              fileName: "qualidade_de_estoque",
              sheetName: "Qualidade de Estoque",
              hideOnExport: ["expand"],
            }}
            uiState={{ useCustomIcon: true, customIconSize: 32 }}
          />
        </div>
    </div>
  );     
};